import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoopingVideo } from '../components/LoopingVideo';
import { c, fmtEstimate, validityDays } from '../theme';
import { Notice, Wordmark } from '../components/Brand';
import { Menu } from '../components/Menu';
import { ReportActions } from '../components/ReportActions';
import { Toast, type ToastState } from '../components/Toast';
import { api, type ReportSummary, type User } from '../api';

type Filter = 'all' | 'delivered' | 'archived';

const TILES: { key: Filter; label: string; gradient: string; tracking: string }[] = [
  { key: 'all', label: 'ALL', gradient: 'linear-gradient(160deg,#E8745F 0%,#B22F30 100%)', tracking: '0.1em' },
  { key: 'delivered', label: 'DELIVERED', gradient: 'linear-gradient(160deg,#E37DC4 0%,#7A3BD6 100%)', tracking: '0.1em' },
  { key: 'archived', label: 'ARCHIVED', gradient: 'linear-gradient(160deg,#6CC7EE 0%,#3F45D0 100%)', tracking: '0.06em' },
];

export function Portfolio({
  user,
  reports,
  loading,
  onChanged,
}: {
  user: User | null;
  reports: ReportSummary[];
  loading: boolean;
  onChanged: () => void;
}) {
  // Delivered is the working list: archiving something should make it leave.
  const [filter, setFilter] = useState<Filter>('delivered');
  const [sheetFor, setSheetFor] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const navigate = useNavigate();

  // A report still assembling, or one that failed, has not been delivered.
  // It lives under All until it finishes.
  const isDelivered = (r: ReportSummary) => r.status === 'ready' && !r.archived;

  const counts = useMemo(
    () => ({
      all: reports.length,
      delivered: reports.filter(isDelivered).length,
      archived: reports.filter((r) => r.archived).length,
    }),
    [reports],
  );

  const inFlight = useMemo(
    () => reports.filter((r) => !r.archived && r.status !== 'ready').length,
    [reports],
  );

  const visible = useMemo(
    () =>
      reports.filter((r) => (filter === 'archived' ? r.archived : filter === 'delivered' ? isDelivered(r) : true)),
    [reports, filter],
  );

  const assembling = reports.find((r) => r.status === 'assembling') ?? null;
  const latestReady = reports.find((r) => r.status === 'ready' && !r.archived) ?? null;
  const sheetReport = reports.find((r) => r.id === sheetFor) ?? null;

  const open = (r: ReportSummary) => navigate(r.status === 'ready' ? `/report/${r.id}` : `/assembling/${r.id}`);

  const setArchived = async (r: ReportSummary, archived: boolean, announce = true) => {
    setBusy('archive');
    try {
      await api.setArchived(r.id, archived);
      setSheetFor(null);
      onChanged();
      if (announce) {
        setToast({
          message: `${r.line1} ${archived ? 'archived' : 'restored'}`,
          action: { label: 'Undo', run: () => void setArchived(r, !archived, false) },
        });
      }
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Could not update that report.',
        tone: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stage>
      {/* 1 — Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '48px var(--gx) 0' }}>
        <Wordmark />
        <Menu
          current="portfolio"
          user={user}
          reportCount={reports.length}
          currentReport={latestReady ? { id: latestReady.id, address: latestReady.address } : null}
          assembling={assembling ? { id: assembling.id, address: assembling.address } : null}
        />
      </div>

      {/* 2 — Stat tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 10,
          padding: '40px 24px 0',
          color: '#fff',
        }}
      >
        {TILES.map((t, i) => (
          <div
            key={t.key}
            className="rb-stat-tile"
            style={{
              position: 'relative',
              overflow: 'hidden',
              isolation: 'isolate',
              borderRadius: 24,
              padding: '18px 14px 16px',
              minHeight: 110,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: 14,
              background: t.gradient,
              backgroundSize: '220% 220%',
              animation: `rbTileDrift ${9 + i * 2}s ease-in-out ${i * -3}s infinite alternate`,
              boxShadow: '0 1px 0 rgba(0,0,0,0.12), 0 10px 22px rgba(31,26,31,0.16), inset 0 1px 0 rgba(255,255,255,0.35)',
            }}
          >
            {/* The moving colour field, untinted. Each tile shows a different band of
                the same clip (top, middle, bottom) so the three never look identical. */}
            <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 0, transform: 'scale(1.7)', transformOrigin: ['center top', 'center center', 'center bottom'][i] ?? 'center' }}>
              <LoopingVideo src="/portfolio-bg.mp4" fallback={t.gradient} />
            </div>
            <div
              style={{
                position: 'relative',
                fontSize: 9,
                letterSpacing: t.tracking,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.8)',
                whiteSpace: 'nowrap',
              }}
            >
              {t.label}
            </div>
            <div style={{ position: 'relative', fontSize: 34, fontWeight: 600, lineHeight: 1, letterSpacing: '-0.02em', textShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
              <CountUp value={counts[t.key]} delay={i * 120} />
            </div>
          </div>
        ))}
      </div>

      {/* 3 — Filter */}
      <div style={{ padding: '22px 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: 4, borderRadius: 999, background: c.creamDeep }}>
          {TILES.map((t) => {
            const active = filter === t.key;
            const muted = !active && counts[t.key] === 0;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 7,
                  padding: '11px 8px',
                  borderRadius: 999,
                  background: active ? c.ink : 'transparent',
                  color: active ? c.cream : muted ? 'rgba(31,26,31,0.4)' : c.ink,
                  transition: 'background .15s ease',
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.label}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? 'rgba(245,238,219,0.6)' : muted ? 'rgba(31,26,31,0.3)' : 'rgba(31,26,31,0.45)',
                  }}
                >
                  {counts[t.key]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 4 — Report cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '26px 24px 44px' }}>
        {loading && reports.length === 0 && (
          <div style={{ fontSize: 12, letterSpacing: '0.16em', color: c.ink5, fontWeight: 600 }}>LOADING…</div>
        )}

        {!loading && visible.length === 0 && (
          <Notice>
            {filter === 'archived'
              ? 'Nothing archived.'
              : // "Nothing here yet" would be wrong when reports are simply
                // filed away, or still being built.
                inFlight > 0
                ? `Nothing delivered yet — ${inFlight} still assembling. Tap All to watch ${inFlight === 1 ? 'it' : 'them'}.`
                : counts.archived > 0
                  ? `Nothing in Delivered — ${counts.archived} report${counts.archived === 1 ? '' : 's'} archived.`
                  : 'Nothing here yet. Search an address to assemble your first report.'}
          </Notice>
        )}

        {visible.map((r) => (
          <ReportCard key={r.id} report={r} onOpen={() => open(r)} onActions={() => setSheetFor(r.id)} />
        ))}
      </div>

      {toast && <Toast toast={toast} onDismiss={() => setToast(null)} />}

      {sheetReport && (
        <ReportActions
          report={sheetReport}
          busy={busy}
          onClose={() => setSheetFor(null)}
          onOpen={() => open(sheetReport)}
          onArchive={() => void setArchived(sheetReport, !sheetReport.archived)}
        />
      )}
    </Stage>
  );
}

function ReportCard({
  report,
  onOpen,
  onActions,
}: {
  report: ReportSummary;
  onOpen: () => void;
  onActions: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  const days = validityDays(report.completedAt ?? report.createdAt);

  // The card's top-left line reflects the report's actual state, not a fixture:
  // assembling and failed reports never claim a validity.
  const state =
    report.status === 'assembling'
      ? 'ASSEMBLING'
      : report.status === 'failed'
        ? 'ASSEMBLY FAILED'
        : days > 0
          ? `VALID · ${days} DAY${days === 1 ? '' : 'S'}`
          : 'EXPIRED · REFRESH';

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        borderRadius: 28,
        padding: '22px 24px 24px',
        background: c.field,
        color: c.ink,
        boxShadow: '0 10px 24px -18px rgba(31,26,31,0.45)',
        cursor: 'pointer',
        opacity: report.archived ? 0.72 : 1,
      }}
    >
      {pressed && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 28,
            background: 'rgba(31,26,31,0.03)',
            // Absolutely positioned, so it paints over anything in normal flow —
            // including the ••• button, which would swallow the tap.
            pointerEvents: 'none',
          }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            fontSize: 9,
            letterSpacing: '0.16em',
            fontWeight: 600,
            color: report.status === 'failed' || days === 0 ? c.red : c.ink,
            whiteSpace: 'nowrap',
          }}
        >
          {state}
        </div>
        <button
          aria-label={`Actions for ${report.line1}`}
          onClick={(e) => {
            e.stopPropagation();
            onActions();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            // Positioned so it stays above the card's press overlay.
            position: 'relative',
            zIndex: 1,
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: c.ink,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2.5,
            flex: 'none',
          }}
        >
          {[0, 1, 2].map((i) => (
            <span key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: c.cream }} />
          ))}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}>
          {report.line1.toUpperCase()}
        </div>
        <div style={{ fontSize: 12, letterSpacing: '0.01em', color: c.ink5 }}>{report.locality.toUpperCase()}</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, paddingTop: 4 }}>
        <div style={{ fontSize: 9, letterSpacing: '0.16em', fontWeight: 600, color: c.red }}>INDICATIVE ESTIMATE</div>
        <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
          {report.status === 'ready' ? fmtEstimate(report.targetPrice) : '—'}
        </div>
      </div>
    </div>
  );
}

/** The portfolio scrolls, so the column has no fixed height. */
function Stage({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rb-stage"
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        background: c.page,
        padding: '40px 0 60px',
      }}
    >
      <div
        className="rb-shell"
        style={{
          width: 390,
          maxWidth: '100%',
          minHeight: 844,
          background: c.screen,
          color: c.ink,
          borderRadius: '40px 40px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Counts up to `value` when it first shows (or changes); still under reduced motion. */
function CountUp({ value, delay = 0 }: { value: number; delay?: number }) {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || value <= 0) {
      setShown(value);
      return;
    }
    let raf = 0;
    const startAt = performance.now() + delay;
    const run = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - startAt) / 900));
      setShown(Math.round(value * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(run);
    };
    setShown(0);
    raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [value, delay]);
  return <>{shown}</>;
}
